import {
  CompanyExtended,
  CompanySettingsRequest,
  CreatePersonioAppRequest,
  PayrollSystemEnum
} from '@pxtra-client-lib-legacy/gen';
import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../../hooks/useStore';
import { Form, FormProps, FormSelect, FormTextField } from '../../../../../lib/components';
import { useOptions } from '../../../../receipts/detailView/shared/useOptions';
import { PersonioConfig } from './personio';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePxtraApiClient } from '../../../../../hooks/usePxtraApiClient';
import { enqueueSnackbar } from 'notistack';
import { getPersonioCheckQueryOptions } from '../../../../../queries/personio';
import { getPersonioDetailsQuery } from '../../../../../queries/company/personio';
import { Box } from '@mui/material';
import { FeedbackAlert } from '../../../../../lib/components/atomic/feedback';
import { PaychexConfig } from './paychex';
import {
  PaychexApiCredentialsUpdateError,
  useFetchPaychexApiCredentials,
  useUpdatePaychexApiCredentials
} from '../../../../../queries/company/paychex';
import { useCompanySettings } from '../../../../../queries/company';

export const payrollSystemList: PayrollSystemEnum[] = [
  'OTHER',
  'DATEV_LODAS',
  'DATEV_LUG',
  'AGENDA',
  'SAGE',
  'PERSONIO',
  'ADDISON',
  'PAYCHEX_PLUS',
  'LOGA3',
  'SAP'
];

export const processingKeyList = ['1', '2'];

export const PayrollSystemEdit: FC<Omit<FormProps, 'children' | 'onSubmit'>> = observer((props) => {
  const apiClient = usePxtraApiClient();
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? 'MISSING_ID';

  // query company settings
  const { data: companySettings } = useCompanySettings(companyStore.company?.id ?? '');
  const payrollSystemOptions = useOptions({
    getItems: { sync: () => payrollSystemList },
    map: (item) => ({ value: item, text: t('company:PAYROLL_SYSTEM', { context: item }) })
  });
  const processingKeyOptions = useOptions({
    getItems: { sync: () => processingKeyList },
    map: (item) => ({ value: item, text: item })
  });

  // Mutation to create the personio details
  const { mutateAsync: createPersonioApp } = useMutation({
    mutationFn: ({
      client_id,
      client_secret,
      company
    }: CreatePersonioAppRequest & {
      company: CompanyExtended['id'];
    }) =>
      apiClient.integrations.companiesPersonioCreate({
        parentCompany: company,
        requestBody: {
          client_id: client_id ?? '',
          client_secret: client_secret ?? ''
        }
      }),
    onSuccess: () => {
      enqueueSnackbar(t('company:PERSONIO_APP_CREATED'), { variant: 'success' });

      queryClient.invalidateQueries({
        queryKey: ['company', companyId, 'personio']
      });
      queryClient.invalidateQueries({
        queryKey: ['company', companyId, 'personio', 'check']
      });
    },
    onError: () => {
      enqueueSnackbar(t('company:PERSONIO_APP_CREATION_FAILED'), {
        variant: 'error'
      });
    }
  });

  const [selectedPayrollSystem, setSelectedPayrollSystem] = useState<PayrollSystemEnum>(
    companySettings?.payroll_system ?? 'OTHER'
  );

  // query to check if the personio details are valid
  const { data: personioStatus, refetch } = useQuery({
    ...getPersonioCheckQueryOptions(companyId),
    enabled: selectedPayrollSystem === 'PERSONIO',
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  // query to get the personio details
  const { data: personioDetails } = getPersonioDetailsQuery(companyId, {
    enabled: selectedPayrollSystem === 'PERSONIO',
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  // Mutation to update the company settings
  const { mutate: updateCompanySettings } = useMutation({
    mutationFn: ({
      company,
      settings
    }: {
      company: CompanyExtended['id'];
      settings: CompanySettingsRequest;
    }) =>
      apiClient.companies.companiesSettingsPartialUpdate({
        id: company,
        requestBody: settings
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['company', companyId, 'settings'] }),
    onError: (err: { body: { errors: string[] } }) => {
      const errorDetails = JSON.stringify(err.body.errors, null, 2) ?? '';
      enqueueSnackbar(t('company:PAYROLL_SYSTEM_UPDATE_FAILED', { errors: errorDetails }), {
        variant: 'error'
      });
    }
  });
  const { data: paychexApiCredentials } = useFetchPaychexApiCredentials(companyId);

  const { mutate: updatePaychexApiCredentials } = useUpdatePaychexApiCredentials(companyId, {
    onSuccess: () => {
      enqueueSnackbar(t('paychex:updateCredentialsSuccess'), { variant: 'success' });
      queryClient.invalidateQueries({
        queryKey: ['company', companyId, 'paychex']
      });
      queryClient.invalidateQueries({
        queryKey: ['company', companyId, 'settings', 'payroll']
      });
    },
    onError: (error: PaychexApiCredentialsUpdateError) => {
      const errorDetails = error.body?.errors ? JSON.stringify(error.body.errors, null, 2) : '';
      enqueueSnackbar(t('paychex:updateCredentialsError_errors', { errors: errorDetails }), {
        variant: 'error'
      });
    }
  });

  return (
    <Box display="flex gap-y-2" flexDirection="column">
      <div className="mb-4 w-fit">
        <FeedbackAlert severity="info" description={t('payroll:payrollSystemUpdateHint')} />
      </div>
      <Form
        {...props}
        formRef={props.formRef}
        onSubmit={async (values) => {
          const {
            personio: { client_id, client_secret },
            settings
          } = values;

          if (selectedPayrollSystem === 'PERSONIO' && personioStatus?.status !== 'success') {
            /**
             * If the selected payroll system is personio and the personio details are not valid
             * then create the personio app before updating the company settings
             */
            await createPersonioApp({
              client_id: client_id ?? '',
              client_secret: client_secret ?? '',
              company: companyId
            }).then(() => refetch());
          } else if (selectedPayrollSystem === 'PAYCHEX_PLUS') {
            updatePaychexApiCredentials({
              api_url: values.paychex.api_url,
              api_key: values.paychex.api_key,
              paychex_company_id: values.paychex.company,
              id: paychexApiCredentials?.id ?? 0
            });
          }
          updateCompanySettings({ company: companyId, settings });
        }}
        initialValues={{
          settings: companySettings,
          personio: personioDetails?.length ? { client_id: personioDetails[0].client_id } : {},
          paychex: paychexApiCredentials
            ? {
                api_url: paychexApiCredentials.api_url,
                company: paychexApiCredentials.paychex_company_id
              }
            : {}
        }}
        useCustomButton
      >
        <>
          {payrollSystemOptions.length > 0 && (
            <FormSelect
              label={t('company:PAYROLL_SYSTEM')}
              xs={3}
              name="settings.payroll_system"
              options={payrollSystemOptions}
              additionalOnChange={(value) => {
                setSelectedPayrollSystem(value as PayrollSystemEnum);
              }}
              aria-label="Payroll System"
            />
          )}
        </>
        <>
          {selectedPayrollSystem.startsWith('DATEV') && (
            <>
              <FormTextField
                label={t('company:DATEV_CONSULTANT_NO')}
                xs={3}
                name="settings.datev_consultant_number"
              />
              <FormTextField
                label={t('company:DATEV_CLIENT_NO')}
                xs={3}
                name="settings.datev_client_number"
              />
              {selectedPayrollSystem === 'DATEV_LODAS' && (
                <FormSelect
                  name="settings.datev_processing_key"
                  label={t('company:DATEV_PROCESSING_KEY')}
                  options={processingKeyOptions}
                  xs={3}
                  aria-label="Datev Processing Key"
                />
              )}
            </>
          )}
          {selectedPayrollSystem == 'AGENDA' && (
            <FormTextField
              label={t('company:AGENDA_CONSULTANT_NO')}
              xs={4}
              name="settings.agenda_client_number"
            />
          )}
          {selectedPayrollSystem == 'SAGE' && (
            <FormTextField
              label={t('company:SAGE_CLIENT_NO')}
              xs={4}
              name="settings.sage_client_number"
            />
          )}
          {selectedPayrollSystem === 'PERSONIO' && (
            <PersonioConfig createPersonioApp={createPersonioApp} />
          )}
          {selectedPayrollSystem === 'PAYCHEX_PLUS' && <PaychexConfig />}
          {selectedPayrollSystem == 'ADDISON' && (
            <FormTextField
              label={t('company:ADDISON_CLIENT_NO')}
              xs={4}
              name="settings.addison_client_number"
            />
          )}
        </>
      </Form>
    </Box>
  );
});
